import React from "react";
import "./Desktop.css";
import { FaLaptop, FaDatabase, FaCloud, FaCode, FaCog, FaUsers, FaChartLine, FaMobileAlt, FaShieldAlt, FaSync, FaTools, FaFileCode, FaNetworkWired, FaLock } from "react-icons/fa";
import electronLogo from "./Electron_Software_Framework_Logo.svg"; // Replace with the actual path
import kotlinLogo from "./1kotiln.png"; // Replace with the actual path
import javaLogo from "./Java-Logo.jpg"; // Replace with the actual path
import pythonLogo from "./Python-logo-notext.svg.png"; // Replace with the actual path

const Desktop = () => {
  return (
    <div className="desktop-container">
      {/* Hero Section */}
      <div className="desktop-hero">
        <h1 className="desktop-hero__title animate-fade-in">Desktop Development</h1>
        <p className="desktop-hero__description animate-slide-up">
          Desktop development focuses on creating software applications that run
          natively on operating systems like Windows, macOS, and Linux. These
          applications leverage the capabilities of the underlying hardware and
          provide a seamless user experience.
        </p>
        <p className="desktop-hero__description animate-slide-up">
          Whether you're building enterprise-grade tools, data analysis platforms,
          or development environments, desktop applications offer unmatched
          performance, security, and reliability.
        </p>
      </div>

      {/* Key Features Section */}
      <div className="desktop-features animate-slide-up">
        <h2 className="desktop-features__title">Key Features:</h2>
        <ul className="desktop-features__list">
          <li className="desktop-features__item">
            <FaLaptop className="desktop-features__icon" /> High performance with native integrations
          </li>
          <li className="desktop-features__item">
            <FaCog className="desktop-features__icon" /> Scalable and maintainable architecture
          </li>
          <li className="desktop-features__item">
            <FaDatabase className="desktop-features__icon" /> Robust data handling with SQL databases
          </li>
          <li className="desktop-features__item">
            <FaCloud className="desktop-features__icon" /> Cloud-enabled functionality using AWS and Firebase
          </li>
          <li className="desktop-features__item">
            <FaCode className="desktop-features__icon" /> Advanced debugging and customization
          </li>
          <li className="desktop-features__item">
            <FaShieldAlt className="desktop-features__icon" /> Enhanced security with encryption and access controls
          </li>
          <li className="desktop-features__item">
            <FaSync className="desktop-features__icon" /> Real-time synchronization with cloud services
          </li>
        </ul>
      </div>

      {/* Technologies Section */}
      <div className="desktop-technologies animate-slide-up">
        <h2 className="desktop-technologies__title">Popular Technologies:</h2>
        <p className="desktop-technologies__description">
          We use the following tools and frameworks for desktop development:
        </p>
        <div className="desktop-technologies__grid">
          <div className="desktop-technologies__item">
            <img src={electronLogo} alt="Electron.js Logo" className="desktop-technologies__icon" />
            <strong className="desktop-technologies__name">Electron.js</strong>
            <p className="desktop-technologies__info">
              Build cross-platform desktop apps using web technologies.
            </p>
          </div>
          <div className="desktop-technologies__item">
            <img src={kotlinLogo} alt="Kotlin Logo" className="desktop-technologies__icon" />
            <strong className="desktop-technologies__name">Kotlin</strong>
            <p className="desktop-technologies__info">
              Modern, statically typed language for native desktop apps.
            </p>
          </div>
          <div className="desktop-technologies__item">
            <img src={javaLogo} alt="Java Logo" className="desktop-technologies__icon" />
            <strong className="desktop-technologies__name">Java</strong>
            <p className="desktop-technologies__info">
              Versatile language for powerful desktop applications.
            </p>
          </div>
          <div className="desktop-technologies__item">
            <img src={pythonLogo} alt="Python Logo" className="desktop-technologies__icon" />
            <strong className="desktop-technologies__name">Python</strong>
            <p className="desktop-technologies__info">
              Great for scripting and building cross-platform tools.
            </p>
          </div>
        </div>
      </div>

      {/* Benefits Section */}
      <div className="desktop-benefits animate-slide-up">
        <h2 className="desktop-benefits__title">Benefits of Desktop Applications:</h2>
        <div className="desktop-benefits__grid">
          <div className="desktop-benefits__item">
            <FaUsers className="desktop-benefits__icon" />
            <strong className="desktop-benefits__name">Native Performance</strong>
            <p className="desktop-benefits__info">
              Seamless user experience with native integrations.
            </p>
          </div>
          <div className="desktop-benefits__item">
            <FaCog className="desktop-benefits__icon" />
            <strong className="desktop-benefits__name">Offline Access</strong>
            <p className="desktop-benefits__info">
              Reliable functionality without an internet connection.
            </p>
          </div>
          <div className="desktop-benefits__item">
            <FaLaptop className="desktop-benefits__icon" />
            <strong className="desktop-benefits__name">Optimized Resources</strong>
            <p className="desktop-benefits__info">
              Efficient use of system resources for better performance.
            </p>
          </div>
          <div className="desktop-benefits__item">
            <FaShieldAlt className="desktop-benefits__icon" />
            <strong className="desktop-benefits__name">Security</strong>
            <p className="desktop-benefits__info">
              Advanced encryption and secure data handling.
            </p>
          </div>
          <div className="desktop-benefits__item">
            <FaChartLine className="desktop-benefits__icon" />
            <strong className="desktop-benefits__name">Scalability</strong>
            <p className="desktop-benefits__info">
              Easily scalable to meet growing business needs.
            </p>
          </div>
          <div className="desktop-benefits__item">
            <FaTools className="desktop-benefits__icon" />
            <strong className="desktop-benefits__name">Customization</strong>
            <p className="desktop-benefits__info">
              Tailored solutions to fit specific requirements.
            </p>
          </div>
        </div>
      </div>

      {/* Use Cases Section */}
      <div className="desktop-use-cases animate-slide-up">
        <h2 className="desktop-use-cases__title">Common Use Cases:</h2>
        <div className="desktop-use-cases__grid">
          <div className="desktop-use-cases__item">
            <strong className="desktop-use-cases__name">Enterprise Tools</strong>
            <p className="desktop-use-cases__info">
              ERP and CRM systems for business operations.
            </p>
          </div>
          <div className="desktop-use-cases__item">
            <strong className="desktop-use-cases__name">Development IDEs</strong>
            <p className="desktop-use-cases__info">
              Tools like IntelliJ IDEA and Eclipse for coding.
            </p>
          </div>
          <div className="desktop-use-cases__item">
            <strong className="desktop-use-cases__name">Data Analysis</strong>
            <p className="desktop-use-cases__info">
              Platforms for data visualization and analytics.
            </p>
          </div>
          <div className="desktop-use-cases__item">
            <strong className="desktop-use-cases__name">Financial Software</strong>
            <p className="desktop-use-cases__info">
              Applications for accounting, trading, and financial planning.
            </p>
          </div>
          <div className="desktop-use-cases__item">
            <strong className="desktop-use-cases__name">Media Production</strong>
            <p className="desktop-use-cases__info">
              Tools for video editing, graphic design, and audio production.
            </p>
          </div>
          <div className="desktop-use-cases__item">
            <strong className="desktop-use-cases__name">Gaming</strong>
            <p className="desktop-use-cases__info">
              High-performance games with immersive experiences.
            </p>
          </div>
        </div>
      </div>

      {/* Security Section */}
      <div className="desktop-security animate-slide-up">
        <h2 className="desktop-security__title">Security Features:</h2>
        <div className="desktop-security__grid">
          <div className="desktop-security__item">
            <FaLock className="desktop-security__icon" />
            <strong className="desktop-security__name">Data Encryption</strong>
            <p className="desktop-security__info">
              Protect sensitive data with advanced encryption techniques.
            </p>
          </div>
          <div className="desktop-security__item">
            <FaShieldAlt className="desktop-security__icon" />
            <strong className="desktop-security__name">Access Control</strong>
            <p className="desktop-security__info">
              Restrict access to authorized users only.
            </p>
          </div>
          <div className="desktop-security__item">
            <FaNetworkWired className="desktop-security__icon" />
            <strong className="desktop-security__name">Network Security</strong>
            <p className="desktop-security__info">
              Secure communication over networks with SSL/TLS.
            </p>
          </div>
        </div>
      </div>

      {/* Future Trends Section */}
      <div className="desktop-future animate-slide-up">
        <h2 className="desktop-future__title">Future Trends in Desktop Development:</h2>
        <div className="desktop-future__grid">
          <div className="desktop-future__item">
            <FaMobileAlt className="desktop-future__icon" />
            <strong className="desktop-future__name">Cross-Platform Development</strong>
            <p className="desktop-future__info">
              Increasing demand for apps that work seamlessly across desktop and mobile.
            </p>
          </div>
          <div className="desktop-future__item">
            <FaCloud className="desktop-future__icon" />
            <strong className="desktop-future__name">Cloud Integration</strong>
            <p className="desktop-future__info">
              Desktop apps leveraging cloud services for storage and processing.
            </p>
          </div>
          <div className="desktop-future__item">
            <FaFileCode className="desktop-future__icon" />
            <strong className="desktop-future__name">AI and Machine Learning</strong>
            <p className="desktop-future__info">
              Integration of AI/ML for smarter and adaptive applications.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Desktop;